import { Component } from '@theme/component';

/** @type {Record<string, { duration: number; easing: string }>} */
const ANIMATION_PRESETS = {
  snap: { duration: 0, easing: 'linear' },
  'ease-in': { duration: 300, easing: 'ease-in' },
  'ease-out': { duration: 300, easing: 'ease-out' },
  'ease-in-out': { duration: 300, easing: 'ease-in-out' },
};

/**
 * Truncates rich text with a read-more toggle when content exceeds a min height.
 * Re-evaluates on resize so mobile can truncate when desktop does not.
 *
 * @typedef {Object} ReadMoreRefs
 * @property {HTMLElement} clip
 * @property {HTMLElement} content
 * @property {HTMLElement} fade
 * @property {HTMLElement} actions
 * @property {HTMLButtonElement} toggle
 * @property {HTMLElement} label
 *
 * @extends {Component<ReadMoreRefs>}
 */
class ReadMoreText extends Component {
  requiredRefs = ['clip', 'content', 'fade', 'actions', 'toggle', 'label'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {Animation | undefined} */
  #animation;

  /** @type {boolean} */
  #isAnimating = false;

  /** @type {number} */
  #expandedClipHeight = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.toggle.addEventListener('click', this.#onToggle);
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this.refs.clip);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.toggle.removeEventListener('click', this.#onToggle);
    this.#resizeObserver?.disconnect();
    this.#animation?.cancel();
  }

  get #minHeight() {
    return parseInt(this.dataset.minHeight, 10) || 80;
  }

  get #thresholdPx() {
    const percent = parseInt(this.dataset.threshold, 10) || 10;
    return this.#minHeight * (percent / 100);
  }

  get #animationConfig() {
    const preset = this.dataset.animation || 'snap';
    return ANIMATION_PRESETS[preset] ?? ANIMATION_PRESETS.snap;
  }

  get #isExpanded() {
    return this.dataset.expanded === 'true';
  }

  #measureContentHeight() {
    const { clip } = this.refs;
    const previousMaxHeight = clip.style.maxHeight;
    const previousOverflow = clip.style.overflow;
    const previousHeight = clip.style.height;

    clip.style.maxHeight = 'none';
    clip.style.overflow = 'visible';
    clip.style.height = 'auto';

    const fullHeight = this.refs.content.scrollHeight;

    clip.style.maxHeight = previousMaxHeight;
    clip.style.overflow = previousOverflow;
    clip.style.height = previousHeight;

    return fullHeight;
  }

  #setCollapsedState(collapsed) {
    this.classList.toggle('read-more-text--collapsed', collapsed);
    this.classList.toggle('read-more-text--expanded', !collapsed);
    this.dataset.expanded = collapsed ? 'false' : 'true';
  }

  #setLabels() {
    const { toggle, label } = this.refs;

    label.textContent = this.#isExpanded
      ? this.dataset.labelLess || 'Show less'
      : this.dataset.labelMore || 'Read more';
    toggle.setAttribute('aria-expanded', this.#isExpanded ? 'true' : 'false');
  }

  #applyClipState() {
    const { clip } = this.refs;
    const minHeight = this.#minHeight;

    clip.style.removeProperty('height');

    if (this.#isExpanded) {
      clip.style.maxHeight = 'none';
      clip.style.overflow = 'visible';
    } else {
      clip.style.maxHeight = `${minHeight}px`;
      clip.style.overflow = 'hidden';
    }
  }

  #update = () => {
    if (this.#isAnimating) return;

    const { clip, toggle, fade, actions } = this.refs;
    const minHeight = this.#minHeight;
    const contentHeight = this.#measureContentHeight();
    const overflow = contentHeight - minHeight;

    this.#expandedClipHeight = contentHeight;

    if (overflow <= this.#thresholdPx) {
      clip.style.maxHeight = 'none';
      clip.style.overflow = 'visible';
      clip.style.removeProperty('height');
      toggle.hidden = true;
      fade.hidden = true;
      actions.hidden = true;
      this.classList.remove('read-more-text--collapsed', 'read-more-text--expanded');
      this.dataset.expanded = 'false';
      this.#setLabels();
      return;
    }

    toggle.hidden = false;
    fade.hidden = false;
    actions.hidden = false;
    this.#setCollapsedState(!this.#isExpanded);
    this.#applyClipState();
    this.#setLabels();
  };

  /**
   * @param {boolean} expand
   */
  #finishToggle(expand) {
    this.#isAnimating = false;
    this.#animation = undefined;
    this.#setCollapsedState(!expand);
    this.refs.clip.style.removeProperty('height');
    this.#applyClipState();
    this.#setLabels();
  }

  /**
   * @param {boolean} expand
   */
  #animateToggle(expand) {
    const { clip } = this.refs;
    const { duration, easing } = this.#animationConfig;
    const minHeight = this.#minHeight;
    const fullHeight = this.#expandedClipHeight || this.#measureContentHeight();
    const startMaxHeight = expand ? minHeight : fullHeight;
    const endMaxHeight = expand ? fullHeight : minHeight;

    if (duration === 0) {
      this.#finishToggle(expand);
      return;
    }

    this.#isAnimating = true;
    this.#animation?.cancel();

    if (!expand) {
      this.#setCollapsedState(true);
    }

    clip.style.overflow = 'hidden';
    clip.style.maxHeight = `${startMaxHeight}px`;
    clip.style.height = 'auto';

    this.#animation = clip.animate(
      { maxHeight: [`${startMaxHeight}px`, `${endMaxHeight}px`] },
      { duration, easing, fill: 'forwards' }
    );

    this.#animation.onfinish = () => {
      this.#finishToggle(expand);
    };
  }

  /**
   * @param {Event} event
   */
  #onToggle = (event) => {
    event.preventDefault();
    this.#animateToggle(!this.#isExpanded);
  };
}

if (!customElements.get('read-more-text')) {
  customElements.define('read-more-text', ReadMoreText);
}
